import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { TicketIcon, HeadphonesIcon, TrendingUpIcon, ShieldCheckIcon, ZapIcon, BarChart3Icon, SmartphoneIcon } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useBranding } from "@/contexts/BrandingContext";
import { getApiEndpoint } from "@/lib/api";
import { getToken, getUser } from "@/lib/auth-storage";
import { useLanguage } from "@/contexts/LanguageContext";

const Index = () => {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const { branding } = useBranding();
  const { t } = useLanguage();

  const handleAccessSystem = () => {
    const token = getToken();
    const userData = getUser();

    if (token && userData) {
      try {
        const user = JSON.parse(userData);
        // Redirecionar baseado no role
        if (user.role === "internal" || user.role === "analyst" || user.role === "admin") {
          navigate("/atendimento");
        } else {
          navigate("/chamados");
        }
      } catch (error) {
        // Se houver erro ao fazer parse, ir para login
        navigate("/login");
      }
    } else {
      // Se não estiver logado, ir para login
      navigate("/login");
    }
  };

  useEffect(() => {
    const token = getToken();
    const userData = getUser();
    setIsLoggedIn(!!token && !!userData);
  }, []);

  const heroPrimary = branding?.primary_color || "#2563eb";
  const heroSecondary = branding?.secondary_color || "#4f46e5";
  const logoSrc = branding?.logo_url
    ? (branding.logo_url.startsWith("http") ? branding.logo_url : getApiEndpoint(branding.logo_url))
    : null;
  const mascotLogo = "/login-assets/tec-it-mascot.jpeg";

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800">
      <Header />
      
      <main className="container mx-auto px-4 max-w-7xl">
        {/* Hero Section */}
        <section className="text-center py-16 md:py-24 mb-20">
          <div className="max-w-6xl mx-auto">
            <div className="relative overflow-hidden rounded-3xl border border-white/60 dark:border-gray-700/60 bg-white/70 dark:bg-gray-800/80 backdrop-blur-sm shadow-xl px-6 py-12 md:px-12">
              <div
                className="absolute inset-0 opacity-30"
                style={{
                  background: `radial-gradient(circle at top, ${heroPrimary}22, transparent 55%), radial-gradient(circle at bottom, ${heroSecondary}22, transparent 55%)`
                }}
              />
              <div className="relative">
                <div className="flex flex-col items-center gap-4 mb-6">
                  <div
                    className={`relative inline-flex items-center justify-center w-24 h-24 md:w-28 md:h-28 rounded-2xl shadow-lg transform hover:scale-105 transition-transform duration-300 overflow-hidden ${logoSrc ? "bg-white dark:bg-gray-800 dark:ring-1 dark:ring-gray-600" : "ring-2 ring-white/50 dark:ring-gray-600"}`}
                    style={!logoSrc ? { background: `linear-gradient(135deg, ${heroPrimary}, ${heroSecondary})` } : undefined}
                  >
                    {logoSrc ? (
                      <img
                        src={logoSrc}
                        alt={branding?.name || "Logo"}
                        className="max-w-full max-h-full object-contain rounded-xl"
                      />
                    ) : (
                      <>
                        <img
                          src={mascotLogo}
                          alt="TEC-IT"
                          className="w-full h-full object-cover object-top scale-110"
                          onError={(e) => {
                            const el = e.target as HTMLImageElement;
                            el.style.display = "none";
                            const fallback = el.nextElementSibling as HTMLElement;
                            if (fallback) fallback.style.display = "flex";
                          }}
                        />
                        <span className="hidden absolute inset-0 items-center justify-center bg-gradient-to-br from-blue-600 to-indigo-600 text-white font-bold text-3xl" aria-hidden>
                          {branding?.name?.charAt(0).toUpperCase() || "T"}
                        </span>
                      </>
                    )}
                  </div>
                  <div className="text-sm uppercase tracking-[0.2em] text-gray-500 dark:text-gray-400">
                    {t.index.serviceCenter}
                  </div>
                </div>

                <h1 className="text-5xl md:text-6xl font-extrabold text-gray-900 dark:text-white mb-4 leading-tight">
                  {branding?.name || "TEC-IT"}{" "}
                  <span
                    className="bg-clip-text text-transparent"
                    style={{
                      backgroundImage: `linear-gradient(90deg, ${heroPrimary}, ${heroSecondary})`
                    }}
                  >
                    ITSM
                  </span>
                </h1>
                <p className="text-lg md:text-xl text-gray-700 dark:text-gray-300 mb-10 max-w-3xl mx-auto leading-relaxed">
                  {t.index.heroSubtitle}
                  <span className="block mt-2 text-gray-600 dark:text-gray-400">
                    {t.index.heroSubtitle2}
                  </span>
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button 
                    size="lg" 
                    onClick={handleAccessSystem}
                    className="text-white px-10 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                    style={{
                      background: `linear-gradient(90deg, ${heroPrimary}, ${heroSecondary})`
                    }}
                  >
                    {t.index.accessSystem}
                  </Button>
                  {isLoggedIn && (
                    <Link to="/novo-chamado">
                      <Button
                        size="lg"
                        variant="outline"
                        className="px-10 py-6 text-lg font-semibold transition-all duration-300 transform hover:-translate-y-1 dark:border-gray-500 dark:text-gray-200 dark:hover:bg-gray-700 dark:hover:border-gray-400"
                        style={{
                          borderColor: heroPrimary,
                          color: heroPrimary
                        }}
                      >
                        {t.index.openTicket}
                      </Button>
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Cards */}
        <section className="mb-24">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border-0 bg-gradient-to-br from-blue-50 to-blue-100/50 dark:from-gray-800 dark:to-gray-800/80 dark:border-gray-700/50">
              <CardHeader className="text-center pb-4">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-xl mb-4 shadow-md">
                  <TicketIcon className="h-8 w-8 text-white" />
                </div>
                <CardTitle className="text-xl font-bold text-gray-900 dark:text-white">{t.index.ticketSystem}</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-center text-gray-700 dark:text-gray-300 leading-relaxed">
                  {t.index.ticketSystemDesc}
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border-0 bg-gradient-to-br from-green-50 to-green-100/50 dark:from-gray-800 dark:to-gray-800/80 dark:border-gray-700/50">
              <CardHeader className="text-center pb-4">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-green-600 rounded-xl mb-4 shadow-md">
                  <HeadphonesIcon className="h-8 w-8 text-white" />
                </div>
                <CardTitle className="text-xl font-bold text-gray-900 dark:text-white">{t.index.support24}</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-center text-gray-700 dark:text-gray-300 leading-relaxed">
                  {t.index.support24Desc}
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border-0 bg-gradient-to-br from-purple-50 to-purple-100/50 dark:from-gray-800 dark:to-gray-800/80 dark:border-gray-700/50">
              <CardHeader className="text-center pb-4">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-600 rounded-xl mb-4 shadow-md">
                  <TrendingUpIcon className="h-8 w-8 text-white" />
                </div>
                <CardTitle className="text-xl font-bold text-gray-900 dark:text-white">{t.index.slaTransparent}</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-center text-gray-700 dark:text-gray-300 leading-relaxed">
                  {t.index.slaTransparentDesc}
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border-0 bg-gradient-to-br from-red-50 to-red-100/50 dark:from-gray-800 dark:to-gray-800/80 dark:border-gray-700/50">
              <CardHeader className="text-center pb-4">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-red-600 rounded-xl mb-4 shadow-md">
                  <ShieldCheckIcon className="h-8 w-8 text-white" />
                </div>
                <CardTitle className="text-xl font-bold text-gray-900 dark:text-white">{t.index.totalSecurity}</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-center text-gray-700 dark:text-gray-300 leading-relaxed">
                  {t.index.totalSecurityDesc}
                </CardDescription>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Main Resources Section */}
        <section className="mb-24">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
              {t.index.mainResources}
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              {t.index.mainResourcesDesc}
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-7xl mx-auto">
            <Card className="p-8 hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-blue-200 dark:hover:border-gray-600 bg-white dark:bg-gray-800 dark:border-gray-700/50">
              <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl mb-6 shadow-md">
                <ZapIcon className="h-7 w-7 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">{t.index.fullManagement}</h3>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                {t.index.fullManagementDesc}
              </p>
            </Card>
            <Card className="p-8 hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-indigo-200 dark:hover:border-gray-600 bg-white dark:bg-gray-800 dark:border-gray-700/50">
              <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl mb-6 shadow-md">
                <BarChart3Icon className="h-7 w-7 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">{t.index.intuitiveDashboard}</h3>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                {t.index.intuitiveDashboardDesc}
              </p>
            </Card>
            <Card className="p-8 hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-purple-200 dark:hover:border-gray-600 bg-white dark:bg-gray-800 dark:border-gray-700/50">
              <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl mb-6 shadow-md">
                <SmartphoneIcon className="h-7 w-7 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">{t.index.mobileFirst}</h3>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                {t.index.mobileFirstDesc}
              </p>
            </Card>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Index;
